import { get, isEqual } from 'lodash';
import MimirService from '../services/mimir-service';

import {
    FETCH_SUGGESTED_DOMAINS_REQUEST,
    FETCH_SUGGESTED_DOMAINS_SUCCESS,
    FETCH_SUGGESTED_DOMAINS_FAILURE,
    UPDATE_SUGGESTED_DOMAINS_REQUEST,
    UPDATE_SUGGESTED_DOMAINS_SUCCESS,
    UPDATE_SUGGESTED_DOMAINS_PARTIAL_SUCCESS,
    UPDATE_SUGGESTED_DOMAINS_FAILURE,
    FETCH_SUGGESTED_DOMAINS_ORG_DOMAINS_REQUEST,
    FETCH_SUGGESTED_DOMAINS_ORG_DOMAINS_SUCCESS,
    FETCH_SUGGESTED_DOMAINS_ORG_DOMAINS_FAILURE,
} from './types/suggested-domains-action-types';

import {
    openDomainManagementBulkSuccessSnackbar,
    openDomainManagementBulkErrorSnackbar,
} from '../actions/snackbar-action-creators';
import { openDomainManagementBulkPane } from '../actions/right-pane-action-creators';
import { fetchDomainsOverview } from '../actions/domains-overview-action-creators';

import {
    suggestedDomainsIsLoadingSelector,
    suggestedDomainsParamsSelector,
    suggestedDomainsTotalPagesSelector,
    suggestedDomainsFilterParamsSelector,
    suggestedDomainsOrgDomainsIsLoadingSelector,
    suggestedDomainsOrgDomainsParamsSelector
} from '../selectors/suggested-domains-selectors';

const DEFAULT_SUGGESTED_DOMAINS_PARAMS = {
    pageNum: 1,
    pageSize: 50,
};

// OrgDomains of suggested domains
function fetchSuggestedDomainsOrgDomainsRequest( params = {} ){
    return {
        params,
        type: FETCH_SUGGESTED_DOMAINS_ORG_DOMAINS_REQUEST
    };
}
function fetchSuggestedDomainsOrgDomainsSuccess( resp ){
    return {
        orgDomains: resp.orgDomains,
        type: FETCH_SUGGESTED_DOMAINS_ORG_DOMAINS_SUCCESS
    };
}
function fetchSuggestedDomainsOrgDomainsFailure( resp ){
    return {
        type: FETCH_SUGGESTED_DOMAINS_ORG_DOMAINS_FAILURE
    };
}
// Fetch Suggested Domains
function fetchSuggestedDomainsRequest( params = {}, filters = {} ){
    return {
        filters,
        params,
        type: FETCH_SUGGESTED_DOMAINS_REQUEST
    };
}
function fetchSuggestedDomainsSuccess( { byId, allIds, pagination } ){
    return {
        allIds,
        byId,
        pagination,
        type: FETCH_SUGGESTED_DOMAINS_SUCCESS,
    };
}
function fetchSuggestedDomainsFailure( error ){
    return {
        error,
        type: FETCH_SUGGESTED_DOMAINS_FAILURE,
    };
}

function fetchSuggestedDomains( params, filters = {} ){
    return ( dispatch, getState, { DATA_API } ) => {
        dispatch( fetchSuggestedDomainsRequest( params, filters ) );
        const api = new MimirService( DATA_API );

        return api.getSuggestedDomains( { ...params, ...filters } ).then(
            ( response ) => dispatch( fetchSuggestedDomainsSuccess( response ) ),
            ( error ) => dispatch( fetchSuggestedDomainsFailure( error ) )
        );
    };
}

export function fetchSuggestedDomainsIfNeeded( params, filters = {} ){
    const fetchParams = {
        ...DEFAULT_SUGGESTED_DOMAINS_PARAMS,
        ...params
    };
    return ( dispatch, getState ) => {
        const state = getState();

        const prevParams = suggestedDomainsParamsSelector( state );
        const prevFilters = suggestedDomainsFilterParamsSelector( state );

        const isLoading = suggestedDomainsIsLoadingSelector( state );
        const totalPages = suggestedDomainsTotalPagesSelector( state );

        const pageNumChanged = ( get( fetchParams, 'pageNum', 0 ) <= totalPages ) || ( totalPages === 0 );
        const fetchOrFilterParamsChanged = !isEqual( fetchParams, prevParams ) || !isEqual( filters, prevFilters );

        if( !isLoading &&
            fetchOrFilterParamsChanged &&
            pageNumChanged
        ){
            return dispatch( fetchSuggestedDomains( fetchParams, filters ) );
        }
        else{
            return Promise.resolve();
        }
    };
}


function fetchSuggestedDomainsOrgDomains( params ){
    return ( dispatch, getState, { DATA_API } ) => {
        dispatch( fetchSuggestedDomainsOrgDomainsRequest( params ) );
        const api = new MimirService( DATA_API );

        return api.getSuggestedDomainsOrgDomains( { ...params } ).then(
            ( response ) => dispatch( fetchSuggestedDomainsOrgDomainsSuccess( response ) ),
            ( error ) => dispatch( fetchSuggestedDomainsOrgDomainsFailure( error ) )
        );
    };
}
export function fetchSuggestedDomainsOrgDomainsIfNeeded( params = {} ){
    return ( dispatch, getState ) => {
        const state = getState();
        const prevParams = suggestedDomainsOrgDomainsParamsSelector( state );
        const isLoading = suggestedDomainsOrgDomainsIsLoadingSelector( state );

        if( !isLoading &&
            !isEqual( params, prevParams )
        ){
            return dispatch( fetchSuggestedDomainsOrgDomains( params ) );
        }
        else{
            return Promise.resolve();
        }
    };
}
export function refreshSuggestedDomains(){
    return ( dispatch, getState ) => {
        const state = getState();
        const params = suggestedDomainsParamsSelector( state );

        if( params ){
            // fetch first page on refresh -- the reducer handles resetting the array of ids we preserve
            const finalParams = {
                ...params,
                pageNum: 1
            };

            return dispatch( fetchSuggestedDomains( finalParams ) );
        }
    };
}


// Update Suggested Domains Bulk
function updateSuggestedDomainsBulkRequest(){
    return {
        type: UPDATE_SUGGESTED_DOMAINS_REQUEST
    };
}
function updateSuggestedDomainsBulkSuccess( { successfulDomains } ){
    return {
        successfulDomains,
        type: UPDATE_SUGGESTED_DOMAINS_SUCCESS,
    };
}
function updateSuggestedDomainsBulkPartialSuccess( { domainErrors, successfulDomains } ){
    return {
        domainErrors,
        successfulDomains,
        type: UPDATE_SUGGESTED_DOMAINS_PARTIAL_SUCCESS,
    };
}
function updateSuggestedDomainsBulkFailure(){
    return {
        type: UPDATE_SUGGESTED_DOMAINS_FAILURE,
    };
}

function formatSuggestedDomainsBulkMetaData( metadata ){
    let domainErrors = {};
    if( metadata.failedDomains ){
        domainErrors['failedDomains'] = metadata.failedDomains;
    }
    if( metadata.notFoundDomains ){
        domainErrors['notFoundDomains'] = metadata.notFoundDomains;
    }
    return domainErrors;
}

function domainsBulkResponseHelper( dispatch, response, domainActionType, domainType ){
    const { data: { data, metadata }, status } = response;
    const successfulDomains = data;
    const domainErrors = formatSuggestedDomainsBulkMetaData( metadata );

    if( status >= 400 ){
        dispatch( openDomainManagementBulkErrorSnackbar( domainActionType ) );
    }
    else if( status >= 200 ){
        dispatch( fetchDomainsOverview() );
        dispatch( refreshSuggestedDomains() );

        if( status === 207 && Object.keys( domainErrors ).length > 0 ){
            dispatch( updateSuggestedDomainsBulkPartialSuccess( { domainErrors, successfulDomains } ) );
            dispatch( openDomainManagementBulkPane( domainActionType ) );
        }
        else{
            const successfulDomainsTotal = successfulDomains.length || 0;

            dispatch( updateSuggestedDomainsBulkSuccess( { successfulDomains } ) );
            dispatch( openDomainManagementBulkSuccessSnackbar( successfulDomainsTotal, domainActionType, domainType ) );
        }
    }
}

function updateSuggestedDomainsBulk( actionType, domains, domainType ){
    return ( dispatch, getState, { DATA_API } ) => {
        dispatch( updateSuggestedDomainsBulkRequest() );

        const api = new MimirService( DATA_API );
        const requestMap = {
            claim: api.claimSuggestedDomains,
            remove: api.removeSuggestedDomains,
        };

        return requestMap[actionType]( domains, domainType ).then(
            ( response ) => {
                domainsBulkResponseHelper( dispatch, response, actionType, domainType );
            },
            ( error ) => {
                dispatch( updateSuggestedDomainsBulkFailure() );
                dispatch( openDomainManagementBulkErrorSnackbar( actionType ) );
            },
        );
    };
}

export function claimSuggestedDomains( domains, domainType ){
    return ( dispatch ) => dispatch( updateSuggestedDomainsBulk( 'claim', domains, domainType ) );
}

export function removeSuggestedDomains( domains ){
    return ( dispatch ) => dispatch( updateSuggestedDomainsBulk( 'remove', domains ) );
}
